// Laying trips out on a month calendar. Pure (no I/O, no colors, no DOM), so
// the whole layout is unit-testable and the React components are left with
// nothing but markup.
//
// A trip is drawn as one bar across the days it covers, and the bar names WHERE
// YOU ARE each day: each leg's destination, in the day columns that leg's stay
// occupies (legStops -> stopCells). Not the trip's name, which the colour-coded
// legend above the grid already carries, and not any `from`, which is only ever
// the previous leg's `to`.
//
// THE DAY-INDEX RULE. Everything here works on a bare calendar-day axis: an
// integer `n` is the day `n` days after 1970-01-01, with no timezone of its
// own. Two projections put things on that axis, and mixing them up is the one
// bug this module exists to prevent:
//
//   A STORED TRIP DATE projects through its UTC year/month/day (utcDayIndex).
//   That is the reading tripGroupDateRange and throughDateFor already use, and
//   a bar has to agree with the row the user clicked to get here.
//
//   TODAY projects through the LOCAL year/month/day (localDayIndex). That's a
//   wall clock, not a record: a UTC "today" would light the wrong cell every
//   evening west of Greenwich.
//
// Both land on the same axis and compare directly.
//
// A consequence worth knowing rather than "fixing": leg dates are stored
// inconsistently -- cascadeLegDates writes local midnight, parseTripExport
// writes UTC midnight, and createTripWithUI stubs a leg at the current instant
// -- so a leg stubbed at 6pm in the Americas stores an instant that is already
// tomorrow in UTC and draws a day late. It also already READS a day late in the
// trips list. Inheriting that beats inventing a second, contradictory answer.

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import localizedFormat from 'dayjs/plugin/localizedFormat.js';
import { utcDayIndex, localDayIndex, tripGroupDateRange } from './TripGroups.mjs';
import { resolvePlaceName } from './PlaceFields.mjs';

dayjs.extend(utc);
dayjs.extend(localizedFormat);

export const DAYS_PER_WEEK = 7;

// Always six, never "as many as this month needs". A grid that changed height
// between months would make Prev/Next jump the page under the pointer.
export const WEEKS_PER_GRID = 6;

const MS_PER_DAY = 86400000;

// ---------------------------------------------------------------------------
// The axis
// ---------------------------------------------------------------------------

export function partsToDayIndex(year, month, date) {
  return Date.UTC(year, month, date) / MS_PER_DAY;
}

export function dayIndexToParts(index) {
  const d = new Date(index * MS_PER_DAY);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth(), date: d.getUTCDate() };
}

// 0 = Sunday. Day index 0 was 1970-01-01, a Thursday, hence the +4; the double
// modulo keeps pre-1970 (negative) indices positive.
export function dayOfWeek(index) {
  return (((index + 4) % DAYS_PER_WEEK) + DAYS_PER_WEEK) % DAYS_PER_WEEK;
}

// One day on the axis, as 'MMM D'. Formatted from the INDEX rather than from a
// stored date, so it can only ever agree with the column the bar is drawn in --
// the day-index rule obeyed for free. No year: every caller has already said it.
export function dayIndexLabel(index) {
  return dayjs.utc(index * MS_PER_DAY).format('MMM D');
}

// ---------------------------------------------------------------------------
// The grid
// ---------------------------------------------------------------------------

// The 42-day window a month is drawn in: the month's days plus enough of the
// neighbouring months to fill whole weeks. `weekStartsOn` is 0 for Sunday, 1
// for Monday.
export function buildMonthGrid(year, month, { weekStartsOn = 0 } = {}) {
  const first = partsToDayIndex(year, month, 1);
  // How far back to the week's start. The +7 keeps it non-negative when the
  // month starts before the configured first day of the week.
  const lead = (dayOfWeek(first) - weekStartsOn + DAYS_PER_WEEK) % DAYS_PER_WEEK;
  const startIndex = first - lead;

  const weeks = [];
  for (let w = 0; w < WEEKS_PER_GRID; w++) {
    const weekStart = startIndex + w * DAYS_PER_WEEK;
    const days = [];
    for (let d = 0; d < DAYS_PER_WEEK; d++) {
      const index = weekStart + d;
      const parts = dayIndexToParts(index);
      days.push({
        index,
        ...parts,
        inMonth: parts.year === year && parts.month === month,
      });
    }
    weeks.push({ weekIndex: w, startIndex: weekStart, days });
  }

  return {
    year,
    month,
    weekStartsOn,
    startIndex,
    endIndex: startIndex + WEEKS_PER_GRID * DAYS_PER_WEEK - 1,
    weeks,
  };
}

// ---------------------------------------------------------------------------
// A trip's footprint
// ---------------------------------------------------------------------------

// The inclusive day span a trip group occupies, or null when it has no usable
// start date (it simply isn't drawn).
//
// The end falls back through throughDate -> endDate -> startDate: a group
// handed over in router state can predate throughDate, and a single-leg group
// has nothing else. Clamped at the start so a corrupt end can only ever produce
// a one-day bar, never a negative span the layout would have to defend against.
//
// The span is CONTINUOUS even when the legs are hand-dated with a gap. That
// span is exactly what tripGroupDateRange already reports for the same group,
// and the stop cells inside it show the real structure -- don't "fix" this
// into a segmented bar that disagrees with the list the user came from.
export function tripGroupDaySpan(group) {
  const startIndex = utcDayIndex(group?.startDate);
  if (startIndex == null) return null;
  const end = utcDayIndex(group?.throughDate ?? group?.endDate ?? group?.startDate);
  return { startIndex, endIndex: end == null ? startIndex : Math.max(startIndex, end) };
}

// Where you are on each day of a trip: one stop per leg, on the day that leg
// happens, named for where that leg ENDS.
//
// `to` is the destination -- the point of the leg -- and a leg's `from` is only
// ever the previous leg's `to`, so naming both would say everything twice. The
// bar is one line high; it gets the half that is news.
//
// The FIRST leg is included, which is what stops this being a rename of the
// leg-divider list it replaced: that one excluded leg 0 by design, because a
// divider on the trip's own first day is the bar's left cap. It still is --
// offset 0 never draws a divider (see stopCells) -- but the first leg's
// destination is where you go on day one and has to be named.
//
// placeIndex-free on purpose: `name` is the RAW stored string, so the component
// shortens it at render and still has the original for a title.
//
// A leg with no `to` still yields a stop with a null name -- it is a real
// boundary and keeps its divider; it just has nothing to say. Two legs on one
// day collapse to ONE stop, the LAST of them, because that is where you end up
// that night. (group.legs is date-ascending, which buildGroup guarantees; the
// sort below only re-establishes the axis order, not that rule.)
export function legStops(group) {
  const byDay = new Map();
  for (const leg of group?.legs || []) {
    const dayIndex = utcDayIndex(leg?.date);
    if (dayIndex == null) continue; // undated: nothing to place it on
    const raw = typeof leg?.to === 'string' ? leg.to.trim() : '';
    byDay.set(dayIndex, { dayIndex, name: raw || null }); // last that day wins
  }
  return [...byDay.values()].sort((a, b) => a.dayIndex - b.dayIndex);
}

// The stop cells covering one segment: a tiling of its columns, one cell per
// stop that OPENS inside it, each running until the next one does.
//
// The cell in force at the segment's first column is the last stop on or before
// that day -- so a stay crossing a Saturday REPEATS its destination on the
// continuation row (carried: true) rather than leaving a blank stripe after the
// chevron. A stop landing exactly on the first column is an ARRIVAL, not a
// carry, and draws no divider: offset 0 is the bar's cap.
//
// `stops` must be ascending; segmentsForGrid sorts once before the week loop.
function stopCells(stops, from, to) {
  let head = null;
  const opens = [];
  for (const stop of stops) {
    if (stop.dayIndex <= from) head = stop;
    else if (stop.dayIndex <= to) opens.push(stop);
  }
  const starts = [
    {
      dayIndex: from,
      name: head?.name ?? null,
      carried: head != null && head.dayIndex < from,
    },
    ...opens.map((s) => ({ dayIndex: s.dayIndex, name: s.name, carried: false })),
  ];
  return starts.map((cell, i) => ({
    offset: cell.dayIndex - from,
    span: (starts[i + 1]?.dayIndex ?? to + 1) - cell.dayIndex,
    name: cell.name,
    carried: cell.carried,
  }));
}

// ---------------------------------------------------------------------------
// Lanes
// ---------------------------------------------------------------------------

// Which row within a week each trip draws on, packed greedily so trips that
// don't overlap share a lane.
//
// Assigned across the WHOLE window rather than per week, and that's the point:
// a trip that sat on row 0 in the first week and row 2 in the third would be
// impossible to follow across a month. Packing globally costs almost nothing --
// two trips a fortnight apart still share lane 0 -- and buys a trip one stable
// row for the month.
//
// Trips that merely touch (one ends the day before the next begins) are allowed
// to share a lane: different colors and rounded caps tell them apart, and
// demanding a gap day would waste a lane for no gain.
export function assignTripLanes(spans) {
  const ordered = [...(spans || [])].sort(
    (a, b) =>
      a.startIndex - b.startIndex ||
      // Longer first, so a long trip claims the low lane and the short ones
      // stack above it rather than fragmenting it.
      b.endIndex - b.startIndex - (a.endIndex - a.startIndex) ||
      String(a.key).localeCompare(String(b.key))
  );

  const laneEnds = []; // laneEnds[i] = the last day index lane i is occupied
  const lanes = new Map();
  for (const span of ordered) {
    let lane = laneEnds.findIndex((end) => end < span.startIndex);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = span.endIndex;
    lanes.set(span.key, lane);
  }
  return lanes;
}

// ---------------------------------------------------------------------------
// Segments
// ---------------------------------------------------------------------------

// A span split at week boundaries -- a bar can't wrap a row, so a trip crossing
// a Saturday becomes two segments that read as one through their flat caps.
//
// Clipped to the GRID, not the month, so a trip living only in the greyed spill
// days still draws. `continuesBefore` covers both "started in an earlier week
// of this grid" and "started before this grid entirely": they want the same
// flat cap, and the distinction that matters (is this cell an arrival or a
// carry?) is the same question either way.
//
// The option is `legStops` and the output field is `stops`, deliberately not
// the same word: they are different shapes (absolute days vs. segment-relative
// cells) and one must never be fed back in as the other.
export function segmentsForGrid(span, grid, { legStops = [] } = {}) {
  if (!span || !grid) return [];
  // Sorted once, not per week: stopCells' head scan is only correct on an
  // ascending list, and a caller will hand it an unsorted literal eventually.
  const ordered = [...legStops].sort((a, b) => a.dayIndex - b.dayIndex);

  const segments = [];
  for (const week of grid.weeks) {
    const weekStart = week.startIndex;
    const weekEnd = weekStart + DAYS_PER_WEEK - 1;
    const from = Math.max(span.startIndex, weekStart);
    const to = Math.min(span.endIndex, weekEnd);
    if (from > to) continue;
    segments.push({
      weekIndex: week.weekIndex,
      startIndex: from,
      endIndex: to,
      startColumn: from - weekStart + 1, // 1-based, feeds grid-column
      span: to - from + 1,
      continuesBefore: span.startIndex < from,
      continuesAfter: span.endIndex > to,
      // Segment-relative, so a cell in week 2 can't render in week 1. Always
      // tiles the segment exactly: stops[0].offset is 0 and the spans sum to
      // segment.span.
      stops: stopCells(ordered, from, to),
    });
  }
  return segments;
}

// ---------------------------------------------------------------------------
// The whole calendar
// ---------------------------------------------------------------------------

// Everything a month view needs, from the groups it should draw.
//
// `groups` are the SELECTED groups; anything the caller filtered out never gets
// here. A group with no usable start date, or one whose span misses this grid
// entirely, lands in `hidden` so the page can say so rather than silently
// dropping it.
export function buildTripCalendar(groups, { year, month, weekStartsOn = 0, today = new Date() } = {}) {
  const grid = buildMonthGrid(year, month, { weekStartsOn });

  const spans = [];
  const hidden = [];
  for (const group of groups || []) {
    const span = tripGroupDaySpan(group);
    if (!span || span.endIndex < grid.startIndex || span.startIndex > grid.endIndex) {
      hidden.push(group);
      continue;
    }
    spans.push({ key: group.key, group, span, stops: legStops(group) });
  }

  const lanes = assignTripLanes(
    spans.map(({ key, span }) => ({ key, startIndex: span.startIndex, endIndex: span.endIndex }))
  );

  const byWeek = grid.weeks.map(() => []);
  for (const { key, group, span, stops } of spans) {
    const laneIndex = lanes.get(key) ?? 0;
    for (const segment of segmentsForGrid(span, grid, { legStops: stops })) {
      byWeek[segment.weekIndex].push({ ...segment, key, group, laneIndex });
    }
  }

  const todayIndex = localDayIndex(today);

  const weeks = grid.weeks.map((week, i) => {
    const segments = byWeek[i].sort((a, b) => a.laneIndex - b.laneIndex || a.startColumn - b.startColumn);
    return {
      weekIndex: week.weekIndex,
      startIndex: week.startIndex,
      label: dayIndexLabel(week.startIndex),
      days: week.days.map((day) => ({ ...day, isToday: day.index === todayIndex })),
      segments,
      // The lane COUNT, not the number of lanes in use: a week whose only bar
      // sits on lane 1 still renders two rows, the first one empty. That empty
      // row is what keeps the bar lined up with its own continuation in the
      // week above. It looks like a bug and isn't.
      laneCount: Math.max(1, ...segments.map((s) => s.laneIndex + 1)),
    };
  });

  const visible = spans
    .filter(({ key }) => weeks.some((w) => w.segments.some((s) => s.key === key)))
    .sort((a, b) => a.span.startIndex - b.span.startIndex)
    .map(({ group }) => group);

  return {
    year,
    month,
    monthLabel: calendarMonthLabel(year, month),
    weekdayHeadings: weekdayHeadings(weekStartsOn),
    startIndex: grid.startIndex,
    endIndex: grid.endIndex,
    todayIndex,
    weeks,
    visible,
    hidden,
  };
}

// ---------------------------------------------------------------------------
// Months: navigation, labels, URL
// ---------------------------------------------------------------------------

export function stepCalendarMonth(year, month, delta) {
  const total = year * 12 + month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

export function calendarMonthLabel(year, month) {
  return dayjs.utc(Date.UTC(year, month, 1)).format('MMMM YYYY');
}

export function weekdayHeadings(weekStartsOn = 0) {
  // Day index 3 (1970-01-04) was a Sunday, so it anchors the names without
  // needing a locale-week plugin.
  const sunday = 3;
  return Array.from({ length: DAYS_PER_WEEK }, (_, i) =>
    dayjs.utc((sunday + weekStartsOn + i) * MS_PER_DAY).format('ddd')
  );
}

// The month the calendar opens on: the one holding the soonest trip it was
// given, or the current month when nothing has a date.
export function initialCalendarMonth(groups, { now = new Date() } = {}) {
  let soonest = null;
  for (const group of groups || []) {
    const span = tripGroupDaySpan(group);
    if (span && (soonest == null || span.startIndex < soonest)) soonest = span.startIndex;
  }
  if (soonest == null) {
    return { year: now.getFullYear(), month: now.getMonth() };
  }
  const { year, month } = dayIndexToParts(soonest);
  return { year, month };
}

export function calendarMonthParam(year, month) {
  return `${String(year).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}`;
}

// Regex-validated, never Number()'d: `Number('2027-06')` is NaN and
// `Number('06')` is 6, so a sloppy parse turns a typo into a silent wrong
// answer. (Same rule LogbookYear applies to its :year segment.)
export function parseCalendarMonthParam(value) {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(String(value ?? ''));
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) - 1 };
}

// ---------------------------------------------------------------------------
// Labels and predicates
// ---------------------------------------------------------------------------

// The accessible name of one bar: everything the sighted reader gets from its
// position, color and cell labels, said out loud. Lives here rather than in the
// component so it's covered by the unit suite.
//
// `stops` is this SEGMENT's cells (segmentsForGrid's output), so the label says
// what this bar draws rather than the whole trip -- a trip crossing a week
// boundary is two buttons, and each should describe itself.
//
// The trip name stays FIRST (an e2e finds a bar by it) and `continued` stays
// LAST (two suites assert /, continued$/). "stops: " announces the list because
// gazetteer names contain commas -- "Roche Harbor, San Juan" -- and so does the
// date range, so bare commas would run the three clauses together.
export function tripCalendarBarLabel(group, { placeIndex, continued = false, stops } = {}) {
  const parts = [group?.name, tripGroupDateRange(group)];
  const named = (stops || [])
    .map((stop) => resolvePlaceName(stop?.name, placeIndex))
    .filter((name) => typeof name === 'string' && name.trim() !== '')
    // Consecutive legs ending at one place say it once.
    .filter((name, i, all) => name !== all[i - 1]);
  if (named.length) parts.push(`stops: ${named.join(', ')}`);
  if (continued) parts.push('continued');
  return parts.filter((p) => p != null && String(p).trim() !== '').join(', ');
}

// Is the trip still running on `dayIndex`? The calendar's own reading of
// isUpcomingTripGroup, in day-index space.
//
// It has to be a separate function: isUpcomingTripGroup compares the raw
// throughDate INSTANT against local start-of-day, so on the last morning of a
// trip stored at UTC midnight it goes false while this module is still drawing
// the bar through today. The item card depends on the old behaviour, so that
// one is left exactly as it is and the calendar asks its question its own way.
export function groupRunsOnOrAfter(group, dayIndex) {
  const span = tripGroupDaySpan(group);
  if (!span || dayIndex == null) return false;
  return span.endIndex >= dayIndex;
}
